import { useEffect, useState } from "react";
import {
  Modal,
  Table,
  Checkbox,
  Button,
  Group,
  Text,
  Loader,
  Stack,
  Badge,
  Code,
  ActionIcon,
} from "@mantine/core";
import { IconChevronDown, IconChevronRight } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { discoverMcpTools, importMcpTools } from "./mcpServerApi";

export default function McpToolDiscovery({ server, onClose }) {
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [expanded, setExpanded] = useState(new Set());
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!server) return;
    setLoading(true);
    setError(null);
    setTools([]);
    setSelected(new Set());
    setExpanded(new Set());

    discoverMcpTools(server.id)
      .then((data) => {
        setTools(data);
        // Pre-select tools not yet imported
        const newSelected = new Set();
        data.forEach((t) => {
          if (!t.alreadyImported) newSelected.add(t.name);
        });
        setSelected(newSelected);
      })
      .catch((err) => setError(err.message || "Failed to connect"))
      .finally(() => setLoading(false));
    // Only server.id matters here; adding the whole `server` object would refetch on every
    // parent re-render that passes a fresh object.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [server?.id]);

  const toggleSelect = (name) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });
  };

  const toggleExpand = (name) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });
  };

  const handleImport = async () => {
    setImporting(true);
    try {
      const result = await importMcpTools(server.id, [...selected]);
      notifications.show({
        title: "Imported",
        message: `${result.length} tool${result.length !== 1 ? "s" : ""} imported successfully`,
        color: "green",
      });
      onClose();
    } catch (err) {
      notifications.show({
        title: "Import failed",
        message: err.message,
        color: "red",
      });
    } finally {
      setImporting(false);
    }
  };

  return (
    <Modal
      opened={server != null}
      onClose={onClose}
      title={`Discover Tools — ${server?.name ?? ""}`}
      size="lg"
    >
      {loading && (
        <Stack align="center" py="xl">
          <Loader />
          <Text size="sm" c="dimmed">
            Connecting to MCP server...
          </Text>
        </Stack>
      )}

      {error && (
        <Text c="red" size="sm">
          {error}
        </Text>
      )}

      {!loading && !error && tools.length === 0 && (
        <Text c="dimmed" size="sm">
          No tools found on this server.
        </Text>
      )}

      {!loading && tools.length > 0 && (
        <Stack>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th w={40}></Table.Th>
                <Table.Th>Tool</Table.Th>
                <Table.Th>Description</Table.Th>
                <Table.Th w={80}>Status</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {tools.map((tool) => (
                <>
                  <Table.Tr key={tool.name}>
                    <Table.Td>
                      <Checkbox
                        checked={selected.has(tool.name)}
                        onChange={() => toggleSelect(tool.name)}
                        disabled={tool.alreadyImported}
                      />
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4}>
                        <ActionIcon
                          variant="subtle"
                          size="xs"
                          onClick={() => toggleExpand(tool.name)}
                        >
                          {expanded.has(tool.name) ? (
                            <IconChevronDown size={14} />
                          ) : (
                            <IconChevronRight size={14} />
                          )}
                        </ActionIcon>
                        <Text size="sm" fw={500}>
                          {tool.name}
                        </Text>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="dimmed" lineClamp={1}>
                        {tool.description || "—"}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      {tool.alreadyImported ? (
                        <Badge variant="light" color="gray" size="sm">
                          Imported
                        </Badge>
                      ) : (
                        <Badge variant="light" color="teal" size="sm">
                          New
                        </Badge>
                      )}
                    </Table.Td>
                  </Table.Tr>
                  {expanded.has(tool.name) && tool.inputSchema && (
                    <Table.Tr key={`${tool.name}-schema`}>
                      <Table.Td colSpan={4}>
                        <Code block style={{ maxHeight: 200, overflow: "auto" }}>
                          {JSON.stringify(tool.inputSchema, null, 2)}
                        </Code>
                      </Table.Td>
                    </Table.Tr>
                  )}
                </>
              ))}
            </Table.Tbody>
          </Table>

          <Group justify="space-between">
            <Text size="sm" c="dimmed">
              {selected.size} tool{selected.size !== 1 ? "s" : ""} selected
            </Text>
            <Button
              onClick={handleImport}
              loading={importing}
              disabled={selected.size === 0}
            >
              Import Selected
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}
